/**
 * @module tracking/pointer
 * Converts MediaPipe HandLandmarkerResult into HandPointer records.
 *
 * Applies per-hand one-euro smoothing, mirrors the X axis to match
 * the mirrored video feed, and maps normalised [0–1] coords to canvas pixels.
 *
 * Landmark 8 (index fingertip) is the pointer for slash detection and
 * dwell-click navigation.
 *
 * ADR-002: filter is applied BEFORE velocity computation. Raw positions
 * are kept in HandPointer.rawX/rawY for the debug overlay only.
 */

import type { HandLandmarkerResult } from '@mediapipe/tasks-vision';
import type { HandPointer } from '../types.js';
import { CONFIG } from '../game-config.js';
import { OneEuroFilter } from './smoothing.js';

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

/** Fingertip landmark index per MediaPipe spec. */
export const INDEX_FINGERTIP_LANDMARK = 8;

/** Per-hand, per-axis one-euro filters — reset when the hand disappears. */
const _filters: [
  { x: OneEuroFilter; y: OneEuroFilter },
  { x: OneEuroFilter; y: OneEuroFilter },
] = [
  {
    x: new OneEuroFilter(CONFIG.slash.smoothing),
    y: new OneEuroFilter(CONFIG.slash.smoothing),
  },
  {
    x: new OneEuroFilter(CONFIG.slash.smoothing),
    y: new OneEuroFilter(CONFIG.slash.smoothing),
  },
];

/** Whether each hand was detected in the previous frame (for reset on re-appearance). */
const _wasDetected: [boolean, boolean] = [false, false];

/** Latest computed hand pointers (updated each tracking frame). */
let _lastPointers: HandPointer[] = [];

// ---------------------------------------------------------------------------
// Public functional API (per module-contracts.md)
// ---------------------------------------------------------------------------

/**
 * Convert a MediaPipe landmark result into HandPointer records.
 * Applies one-euro smoothing, maps normalised coords to canvas pixels,
 * and mirrors the X axis.
 *
 * Returns an array of length 0, 1, or 2 depending on detected hands.
 */
export function landmarksToPointers(
  result: HandLandmarkerResult,
  canvasWidth: number,
  canvasHeight: number,
  timestamp: number,
): HandPointer[] {
  const pointers: HandPointer[] = [];
  const tsSec = timestamp / 1000;

  const detected: [boolean, boolean] = [false, false];

  for (let i = 0; i < Math.min(result.landmarks.length, 2); i++) {
    const handIndex = i as 0 | 1;
    const landmarks = result.landmarks[i];
    if (!landmarks) continue;

    const tip = landmarks[INDEX_FINGERTIP_LANDMARK];
    if (!tip) continue;

    detected[handIndex] = true;

    // Reset filter if hand just re-appeared to avoid stale-state velocity spike.
    if (!_wasDetected[handIndex]) {
      _filters[handIndex].x.reset();
      _filters[handIndex].y.reset();
    }

    // Raw normalised coords (before smoothing) — mirror X for natural feel.
    const rawNormX = 1 - tip.x;
    const rawNormY = tip.y;

    // Apply one-euro filter (ADR-002: filter first, velocity second).
    const filteredNormX = _filters[handIndex].x.filter(rawNormX, tsSec);
    const filteredNormY = _filters[handIndex].y.filter(rawNormY, tsSec);

    pointers.push({
      handIndex,
      x: filteredNormX * canvasWidth,
      y: filteredNormY * canvasHeight,
      rawX: rawNormX * canvasWidth,
      rawY: rawNormY * canvasHeight,
      timestamp,
    });
  }

  // Update detection state for next frame.
  _wasDetected[0] = detected[0];
  _wasDetected[1] = detected[1];

  _lastPointers = pointers;
  return pointers;
}

/**
 * Returns the last computed HandPointer array (read by render loop for interpolation).
 * Returns a copy — never mutates internal state.
 */
export function getHandPointers(): readonly HandPointer[] {
  return [..._lastPointers];
}

/** Reset all filter states and pointer history (call on game restart). */
export function resetPointers(): void {
  for (const filterPair of _filters) {
    filterPair.x.reset();
    filterPair.y.reset();
  }
  _wasDetected[0] = false;
  _wasDetected[1] = false;
  _lastPointers = [];
}

// ---------------------------------------------------------------------------
// Legacy export (backwards compat with main.ts PoC)
// ---------------------------------------------------------------------------

/** @deprecated Use `landmarksToPointers` instead. */
export interface NormPoint {
  x: number;
  y: number;
  z: number;
}

/** @deprecated Use `landmarksToPointers` instead. */
export interface PointerPosition {
  x: number;
  y: number;
}

/**
 * @deprecated Use `landmarksToPointers` instead.
 * Converts a normalised landmark point to canvas-space pixels (mirrored X).
 */
export function normToScreen(
  point: NormPoint,
  canvasWidth: number,
  canvasHeight: number,
): PointerPosition {
  return {
    x: (1 - point.x) * canvasWidth,
    y: point.y * canvasHeight,
  };
}
